/**
 * Verification Code Service
 * Handles generation, storage, and validation of verification codes
 */

class VerificationCodeService {
  constructor() {
    this.codes = new Map(); // Store codes with timestamp
    this.CODE_EXPIRY_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds
    this.MAX_ATTEMPTS = 3;
    this.attempts = new Map();
  }

  /**
   * Generate a 6-digit verification code
   */
  generateCode() {
    return Math.floor(100000 + Math.random() * 900000)?.toString();
  }

  /**
   * Store verification code with timestamp
   */
  storeCode(email, code) {
    this.codes?.set(email, {
      code,
      timestamp: Date.now(),
      attempts: 0
    });
  }

  /**
   * Send verification code via email
   * In production, this would call an actual email service API
   */
  async sendVerificationCode(email) {
    try {
      const code = this.generateCode();
      this.storeCode(email, code);

      // Simulate email sending delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // In production, integrate with email service (SendGrid, AWS SES, etc.)
      console.log(`[VERIFICATION CODE] Email: ${email}, Code: ${code}`);
      console.log(`⚠️ DEVELOPMENT MODE: Check console for verification code`);

      // For development, also show alert (remove in production)
      if (import.meta.env?.DEV) {
        alert(`MODO DESARROLLO: Código de verificación: ${code}\n\nEn producción, este código se enviará al correo ${email}`);
      }

      return {
        success: true,
        message: 'Código de verificación enviado exitosamente'
      };
    } catch (error) {
      console.error('Error sending verification code:', error);
      return {
        success: false,
        message: 'Error al enviar el código de verificación'
      };
    }
  }

  /**
   * Validate verification code
   */
  validateCode(email, inputCode) {
    const storedData = this.codes?.get(email);

    if (!storedData) {
      return {
        valid: false,
        message: 'No se encontró código de verificación. Solicita uno nuevo.'
      };
    }

    // Check if code has expired
    const timeElapsed = Date.now() - storedData?.timestamp;
    if (timeElapsed > this.CODE_EXPIRY_TIME) {
      this.codes?.delete(email);
      return {
        valid: false,
        message: 'El código de verificación ha expirado. Solicita uno nuevo.'
      };
    }

    // Check attempts
    if (storedData?.attempts >= this.MAX_ATTEMPTS) {
      this.codes?.delete(email);
      return {
        valid: false,
        message: 'Has excedido el número máximo de intentos. Solicita un nuevo código.'
      };
    }

    // Validate code
    if (storedData?.code === inputCode?.trim()) {
      this.codes?.delete(email); // Remove code after successful validation
      return {
        valid: true,
        message: 'Código verificado exitosamente'
      };
    } else {
      // Increment attempts
      storedData.attempts += 1;
      this.codes?.set(email, storedData);
      
      const remainingAttempts = this.MAX_ATTEMPTS - storedData?.attempts;
      return {
        valid: false,
        message: `Código incorrecto. Te quedan ${remainingAttempts} intento(s).`
      };
    }
  }

  /**
   * Get remaining time for code expiry
   */
  getRemainingTime(email) {
    const storedData = this.codes?.get(email);
    if (!storedData) return 0;

    const timeElapsed = Date.now() - storedData?.timestamp;
    const remainingTime = this.CODE_EXPIRY_TIME - timeElapsed;
    
    return Math.max(0, Math.floor(remainingTime / 1000)); // Return seconds
  }

  /**
   * Clear code for email
   */
  clearCode(email) {
    this.codes?.delete(email);
  }
}

// Export singleton instance
export default new VerificationCodeService();